import { getOperationName } from '@apollo/client/utilities'
import { useDebounce, useToggle } from 'ahooks'
import { Avatar, Button, Form, Input, Modal, Select, message } from 'antd'
import { FormInstance } from 'antd/lib/form'
import { difference } from 'lodash'
import React from 'react'
import { useTranslation } from 'react-i18next'

import {
  GetWebsiteCategoriesDocument,
  UserInfoFragment,
  WebsiteInfoFragment,
  useSearchUserQuery,
  useWebsiteAddMutation,
  useWebsiteDeleteMutation,
  useWebsiteUpdateMutation,
} from '@/generated/graphql'

export const WebsiteCreataEditForm: React.FC<{
  form: FormInstance<any>
  website?: WebsiteInfoFragment
}> = ({ form, website }) => {
  const { t } = useTranslation()
  const [users, setUsers] = React.useState<UserInfoFragment[]>([])
  const [search, setSearch] = React.useState<string>('')
  const debouncedSearch = useDebounce(search, { wait: 600 })

  const { data, loading } = useSearchUserQuery({
    fetchPolicy: 'no-cache',
    skip: !debouncedSearch,
    variables: {
      filter: {
        search: debouncedSearch,
      },
      pagination: {
        first: 6,
      },
    },
  })

  React.useEffect(() => {
    form.resetFields()
  }, [])

  const initialValues = website
    ? {
        title: website.title,
        description: website.description,
        url: website.url,
        maintainerUserIds: website.maintainers.map((maintainer) => maintainer.userId),
      }
    : {
        title: '',
        description: '',
        url: '',
        maintainerUserIds: [],
      }

  const searchedUsers = data
    ? data.users.edges
        .slice()
        .reverse()
        .map(({ node }) => node)
    : website
      ? website.maintainers.slice()
      : []

  const userSelect = (
    <Select
      mode='multiple'
      showSearch
      filterOption={false}
      placeholder={t('search.searchUser')}
      defaultActiveFirstOption={false}
      loading={loading}
      value={users.map((user) => user.userId)}
      onSearch={(value) => setSearch(value)}
      onChange={(userIds) => {
        const storedUserIds = users.map((user) => user.userId)
        const usersToInsert = difference(userIds, storedUserIds)
        const usersToDelete = difference(storedUserIds, userIds)

        const newUsers = searchedUsers.filter((user) => usersToInsert.includes(user.userId))
        const filteredStoredUsers = users.slice().filter((user) => !usersToDelete.includes(user.userId))

        setUsers([...filteredStoredUsers, ...newUsers])
      }}
    >
      {searchedUsers.map((user) => (
        <Select.Option key={user.userId} value={user.userId}>
          <Avatar style={{ marginRight: 4 }} size={16} src={user.avatarUrl} />
          {user.name}
        </Select.Option>
      ))}
    </Select>
  )

  return (
    <>
      <Form
        form={form}
        initialValues={initialValues}
        labelCol={{ span: 3 }}
        wrapperCol={{ offset: 1 }}
        validateMessages={{
          required: t('group.createValidation'),
        }}
      >
        <Form.Item label={t('website.form.title')} name='title' rules={[{ required: true }]}>
          <Input />
        </Form.Item>
        <Form.Item label={t('website.form.description')} name='description'>
          <Input.TextArea showCount maxLength={100} autoSize={false} rows={1} />
        </Form.Item>
        <Form.Item label={t('website.form.url')} name='url' rules={[{ required: true }]}>
          <Input />
        </Form.Item>
        <Form.Item label={t('website.form.maintainerUserIds')} name='maintainerUserIds' rules={[{ required: true }]}>
          {userSelect}
        </Form.Item>
      </Form>
    </>
  )
}

export const WebsiteCreateEditModal: React.FC<{
  websiteCategoryId: number
  website?: WebsiteInfoFragment
}> = ({ websiteCategoryId, website, children, ...props }) => {
  const { t } = useTranslation()
  const [form] = Form.useForm()
  const [visible, { toggle }] = useToggle()

  const refetchQueries = {
    refetchQueries: [getOperationName(GetWebsiteCategoriesDocument)!],
  }

  const [websiteAdd] = useWebsiteAddMutation({
    ...refetchQueries,
  })

  const [websiteUpdate] = useWebsiteUpdateMutation({
    ...refetchQueries,
  })

  const [websiteDelete] = useWebsiteDeleteMutation({
    ...refetchQueries,
  })

  const onOK = async () => {
    const values = await form.validateFields()

    const params = {
      title: values.title,
      description: values.description,
      url: values.url,
      websiteCategoryId,
      maintainerUserIds: values.maintainerUserIds,
    }
    let res
    if (website) {
      res = await websiteUpdate({
        variables: {
          websiteId: website.websiteId,
          websiteUpdateInput: {
            ...params,
          },
        },
      })
    } else {
      res = await websiteAdd({
        variables: {
          websiteCreateInput: {
            ...params,
          },
        },
      })
    }

    if (!res.data) {
      return
    }

    toggle()
  }

  children = React.cloneElement(children as any, {
    ...props,
    onClick: () => toggle(),
  })

  const footer = (
    <div
      style={{
        display: 'flex',
        justifyContent: website ? 'space-between' : 'flex-end',
      }}
    >
      {website && (
        <div>
          <Button
            danger
            onClick={async () => {
              const res = await websiteDelete({
                variables: { websiteId: website.websiteId },
              })
              if (res.data) {
                message.success(t('website.deleteSucceed'))
              }
              toggle()
            }}
          >
            {t('website.form.delete')}
          </Button>
        </div>
      )}
      <div>
        <Button onClick={() => toggle()}>{t('website.form.cancel')}</Button>
        <Button type='primary' onClick={() => onOK()}>
          {t('website.form.save')}
        </Button>
      </div>
    </div>
  )

  return (
    <>
      {children}
      <Modal
        footer={footer}
        title={t(`website.${!website ? 'addSystem' : 'updateSystem'}`)}
        bodyStyle={{ padding: 40 }}
        maskClosable={false}
        visible={visible}
        closable={false}
        destroyOnClose
      >
        <WebsiteCreataEditForm form={form} website={website} />
      </Modal>
    </>
  )
}
